import { type FC, useEffect } from "react"

import { useTranslation } from "react-i18next"

import ReplayIcon from "@mui/icons-material/Replay"
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined"
import { Avatar, Button, Typography } from "@synapse-analytics/synapse-ui"

import type { MessageComponentProps } from "../interfaces"

import styles from "./Message.module.scss"

/**
 * Message Component
 *
 * This component is responsible for rendering a chat message sent by either a user or a bot.
 * It handles message content, displays loading states, manages streaming for bot responses,
 * and supports rich text formatting using Markdown and HTML.
 *
 * @param {Props} props - The properties for the Message component.
 * @returns {JSX.Element} The rendered message component.
 */
export const Message: FC<MessageComponentProps> = ({
  sender,
  content = "",
  isLoading,
  hasError,
  onRetry,
  scrollToBottom,
}) => {
  const { t } = useTranslation("KonanAssistant")
  const isBot = sender === "bot"

  /**
   * Scroll to the bottom of the message container when content changes or new messages arrive.
   * This ensures that the user always sees the latest message, even if it’s streamed.
   */
  useEffect(() => {
    scrollToBottom?.()
  }, [content, scrollToBottom])

  return (
    <div className={styles.root} aria-label={sender}>
      <div className={styles.avatarContainer}>
        {isBot ? (
          <div className={styles.botAvatar}>
            <TipsAndUpdatesOutlinedIcon fontSize="small" style={{ color: "var(--primary-inverse-icon-default)" }} />
          </div>
        ) : (
          // Renders the user's avatar. Uses the auth utility to retrieve the user's full name.
          <Avatar
            name={t("message.userAvatar")}
            isLoading={false}
            isNameInitials
            size="regular"
            isTextVisible={false}
          />
        )}
      </div>

      <div className={styles.messageWrapper}>
        {hasError && (
          <div className={styles.errorContainer}>
            <div className={styles.errorMessage}>
              <Typography color="negative" variant="label-regular" variantColor={2}>
                {t("message.errorMessage")}
              </Typography>
            </div>

            <Button
              size="small"
              startIcon={<ReplayIcon fontSize="small" style={{ width: "14px", height: "14px" }} />}
              onClick={onRetry}
            >
              {t("message.retryButton")}
            </Button>
          </div>
        )}

        <div className={styles.message} aria-label={sender} aria-busy={isLoading}>
          <Typography variant="p">{content}</Typography>
        </div>
      </div>
    </div>
  )
}
